import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Search,
  Refresh,
  Edit,
  Delete,
  Add,
  Person,
  Visibility,
  VisibilityOff,
  Save,
  Cancel,
  Block,
  CheckCircle,
  PersonOff,
  PersonAdd,
} from '@mui/icons-material';
import { accountAPI, Personnel, PersonnelFormData, commonAPI, DepartmentOption } from '../../services/api';

// 類型已從 api.ts 導入

const PersonnelManagementPage: React.FC = () => {
  const [personnel, setPersonnel] = useState<Personnel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPersonnel, setEditingPersonnel] = useState<Personnel | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [formData, setFormData] = useState<PersonnelFormData>({
    username: '',
    name: '',
    email: '',
    department_id: 1,
    is_active: true,
    permission: 1,
  });
  
  // 確認對話框狀態
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    action: () => {},
  });
  
  // 部門相關狀態
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [deptLoading, setDeptLoading] = useState(false);

  // 獲取人員列表
  const fetchPersonnel = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await accountAPI.getPersonnel();
      setPersonnel(data);
    } catch (err: any) {
      setError(err.response?.data?.message || '獲取人員列表失敗');
    } finally {
      setLoading(false);
    }
  };

  // 獲取部門列表
  const loadDepartments = async () => {
    try {
      setDeptLoading(true);
      const data = await commonAPI.getDepartments();
      setDepartments(data);
    } catch (e) {
      console.error('載入部門失敗:', e);
    } finally {
      setDeptLoading(false);
    }
  };

  useEffect(() => {
    fetchPersonnel();
    loadDepartments();
  }, []);

  // 篩選人員
  const filteredPersonnel = personnel.filter(person => {
    const matchesSearch = person.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         person.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         person.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && person.is_active) ||
                         (statusFilter === 'inactive' && !person.is_active);
    return matchesSearch && matchesStatus;
  });

  // 處理新增/編輯
  const handleAddPersonnel = () => {
    setEditingPersonnel(null);
    setDialogError(null);
    setUsernameError(null);
    setEmailError(null);
    setFormData({
      username: '',
      name: '',
      email: '',
      department_id: 1,
      is_active: true,
      permission: 1,
    });
    setOpenDialog(true);
  };

  const handleEditPersonnel = (person: Personnel) => {
    setEditingPersonnel(person);
    setDialogError(null);
    setUsernameError(null);
    setEmailError(null);
    setFormData({
      username: person.username,
      name: person.name,
      email: person.email,
      department_id: person.department_id,
      is_active: person.is_active,
      permission: person.permission,
    });
    setOpenDialog(true);
  };

  const handleSavePersonnel = async () => {
    try {
      setDialogError(null);
      setUsernameError(null);
      setEmailError(null);
      if (editingPersonnel) {
        // 更新人員
        await accountAPI.updatePersonnel(editingPersonnel.id, formData);
        alert('人員資料修改成功！');
      } else {
        // 新增人員
        await accountAPI.createPersonnel(formData);
        alert('人員新增成功！');
      }
      setOpenDialog(false);
      fetchPersonnel();
    } catch (err: any) {
      // 只在對話框右側顯示錯誤，不使用頁面級錯誤
      const status = err?.response?.status;
      const serverMsg: string = err?.response?.data?.error || err?.response?.data?.message || '';
      const fieldErrors: any = err?.response?.data?.errors;

      // 後端同時回傳多欄位錯誤時，逐欄位顯示
      if (fieldErrors) {
        if (fieldErrors.username) setUsernameError(fieldErrors.username);
        if (fieldErrors.email) setEmailError(fieldErrors.email);
      }

      // 手機號碼（username）相關錯誤提示
      if (/用戶名已存在|username.*已存在/.test(serverMsg)) {
        setUsernameError('手機號碼已存在，請改用其他號碼');
      } else if (/username 不可為空|username.*不可為空/.test(serverMsg)) {
        setUsernameError('手機號碼不可為空');
      }
      // 電子郵件相關錯誤提示
      if (/電子郵件已存在|email.*已存在|Duplicate.*email|1062.*email/.test(serverMsg)) {
        setEmailError('電子郵件已存在，請改用其他 Email');
      } else if (/email 不可為空|email.*不可為空/.test(serverMsg)) {
        setEmailError('電子郵件不可為空');
      }

      setDialogError('儲存失敗');
    }
  };

  const handleDeletePersonnel = (person: Personnel) => {
    setConfirmDialog({
      open: true,
      title: '確認刪除',
      message: `確定要刪除「${person.name}」嗎？此操作無法復原。`,
      action: async () => {
        try {
          await accountAPI.deletePersonnel(person.id);
          alert('人員刪除成功！');
          fetchPersonnel();
          setConfirmDialog(prev => ({ ...prev, open: false }));
        } catch (err: any) {
          setError(err.response?.data?.message || '刪除失敗');
        }
      },
    });
  };

  const handleToggleStatus = (person: Personnel) => {
    const action = person.is_active ? '停用' : '啟用';
    setConfirmDialog({
      open: true,
      title: `確認${action}`,
      message: `確定要${action}「${person.name}」嗎？`,
      action: async () => {
        try {
          // 只更新 is_active 欄位，保持其他資料不變
          await accountAPI.updatePersonnel(person.id, { 
            username: person.username,
            name: person.name,
            email: person.email,
            department_id: person.department_id,
            is_active: !person.is_active,
            permission: person.permission
          });
          alert(`${action}成功！`);
          fetchPersonnel();
          setConfirmDialog(prev => ({ ...prev, open: false }));
        } catch (err: any) {
          setError(err.response?.data?.message || '狀態更新失敗');
        }
      },
    });
  };

  // 獲取權限文字
  const getPermissionText = (permission: number) => {
    if (permission >= 100) return '管理員';
    if (permission >= 50) return '主管';
    return '一般用戶';
  };

  // 獲取權限顏色
  const getPermissionColor = (permission: number) => {
    if (permission >= 100) return 'error';
    if (permission >= 50) return 'warning';
    return 'success';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
        <CardContent sx={{ p: 0 }}>
          {/* 搜尋和篩選 */}
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            alignItems: 'center',
            p: 2,
            borderBottom: '1px solid #e0e0e0'
          }}>
            <TextField
              placeholder="搜尋姓名、手機號碼或電子郵件..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ flexGrow: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>狀態篩選</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="狀態篩選"
              >
                <MenuItem value="all">全部</MenuItem>
                <MenuItem value="active">啟用</MenuItem>
                <MenuItem value="inactive">停用</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchPersonnel}
            >
              重新整理
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddPersonnel}
            >
              新增人員
            </Button>
          </Box>

          {/* 錯誤提示 */}
          {error && (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          )}

          {/* 人員列表表格 */}
          <TableContainer sx={{ border: '2px solid #e0e0e0', borderRadius: 2 }}>
            <Table sx={{ 
              '& .MuiTableCell-root': {
                border: '1px solid #e0e0e0',
                fontSize: '1.1rem',
                padding: '16px 8px'
              }
            }}>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(135deg, #424242 0%, #616161 100%)' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', fontSize: '1.2rem' }}>ID</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', fontSize: '1.2rem' }}>姓名</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', fontSize: '1.2rem' }}>手機號碼</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', fontSize: '1.2rem' }}>電子郵件</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', fontSize: '1.2rem' }}>部門</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', fontSize: '1.2rem' }}>權限</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', fontSize: '1.2rem' }}>狀態</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', fontSize: '1.2rem' }}>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredPersonnel.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h6" color="text.secondary">
                        目前沒有資料
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPersonnel.map((person) => (
                    <TableRow key={person.id} hover>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '1.1rem' }}>
                          {person.id}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                          {person.name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace', color: 'primary.main', fontSize: '1.1rem' }}>
                          {person.username}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                          {person.email}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                          {person.department_name || `部門 ${person.department_id}`}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Chip
                          label={getPermissionText(person.permission)}
                          color={getPermissionColor(person.permission) as any}
                          size="medium"
                          sx={{ fontWeight: 'bold', fontSize: '1rem' }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Chip
                          label={person.is_active ? '啟用' : '停用'}
                          color={person.is_active ? 'success' : 'error'}
                          size="medium"
                          sx={{ fontWeight: 'bold', fontSize: '1rem' }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="編輯">
                            <IconButton
                              size="medium"
                              onClick={() => handleEditPersonnel(person)}
                              color="primary"
                            >
                              <Edit fontSize="medium" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={person.is_active ? '停用' : '啟用'}>
                            <IconButton
                              size="medium"
                              onClick={() => handleToggleStatus(person)}
                              color={person.is_active ? 'warning' : 'success'}
                            >
                              {person.is_active ? <Block fontSize="medium" /> : <CheckCircle fontSize="medium" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="刪除">
                            <IconButton
                              size="medium"
                              onClick={() => handleDeletePersonnel(person)}
                              color="error"
                            >
                              <Delete fontSize="medium" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* 新增/編輯對話框 */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography component="span" variant="h6">
              {editingPersonnel ? '編輯人員' : '新增人員'}
            </Typography>
            {dialogError && (
              <Typography component="span" variant="body2" color="error" sx={{ fontWeight: 'bold' }}>
                {dialogError}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="姓名"
              value={formData.name}
              onChange={(e) => { setFormData({ ...formData, name: e.target.value }); setDialogError(null); }}
              fullWidth
              required
            />
            <TextField
              label="手機號碼"
              value={formData.username}
              onChange={(e) => { 
                setFormData({ ...formData, username: e.target.value }); 
                setDialogError(null); 
                if (usernameError) setUsernameError(null);
              }}
              fullWidth
              required
              error={!!usernameError}
              helperText={usernameError || ''}
            />
            <TextField
              label="電子郵件"
              type="email"
              value={formData.email}
              onChange={(e) => { 
                setFormData({ ...formData, email: e.target.value }); 
                setDialogError(null); 
                if (emailError) setEmailError(null);
              }}
              fullWidth
              required
              error={!!emailError}
              helperText={emailError || ''}
            />
            <Box display="flex" alignItems="center" gap={1}>
              <FormControl fullWidth>
                <InputLabel>部門</InputLabel>
                <Select
                  value={formData.department_id}
                  onChange={(e) => { setFormData({ ...formData, department_id: Number(e.target.value) }); setDialogError(null); }}
                  label="部門"
                  disabled={deptLoading}
                >
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton 
                aria-label="重新整理部門" 
                onClick={loadDepartments} 
                disabled={deptLoading}
                sx={{ mt: 1 }}
              >
                <Refresh />
              </IconButton>
            </Box>
            <FormControl fullWidth>
              <InputLabel>權限</InputLabel>
              <Select
                value={formData.permission}
                onChange={(e) => { setFormData({ ...formData, permission: Number(e.target.value) }); setDialogError(null); }}
                label="權限"
              >
                <MenuItem value={1}>一般用戶</MenuItem>
                <MenuItem value={50}>主管</MenuItem>
                <MenuItem value={100}>管理員</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => { setFormData({ ...formData, is_active: e.target.checked }); setDialogError(null); }}
                />
              }
              label="啟用帳號"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>
            取消
          </Button>
          <Button onClick={handleSavePersonnel} variant="contained" startIcon={<Save />}>
            儲存
          </Button>
        </DialogActions>
      </Dialog>

      {/* 確認對話框 */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ fontSize: '1.1rem', mt: 1 }}>
            {confirmDialog.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
            variant="outlined"
            sx={{ fontSize: '1rem' }}
          >
            取消
          </Button>
          <Button 
            onClick={confirmDialog.action}
            variant="contained"
            color="primary"
            sx={{ fontSize: '1rem' }}
          >
            確認
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PersonnelManagementPage;
